'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Eye,
  Lightning,
  Database,
  CaretDown,
  DownloadSimple,
  X,
  FloppyDisk,
  ArrowsLeftRight,
  ShareNetwork,
} from '@phosphor-icons/react';
import { Button } from '../ui/Button';
import { DiffResultView, type DiffResultViewRef } from './DiffResultView';
import type { WorkflowExecutionResult } from '../../types/template';

interface WorkflowExecutionResultProps {
  // 输入文件信息（可选）
  inputFile?: {
    name: string;
    rowCount: number;
    columns: string[];
    data: Record<string, unknown>[];
  } | null;
  // 执行结果
  result: WorkflowExecutionResult | null;
  // 是否显示保存工作流按钮
  showSaveWorkflow?: boolean;
  // 保存工作流回调
  onSaveWorkflow?: () => void;
  // 下载结果回调
  onDownloadResult?: () => void;
  // 关闭回调
  onClose?: () => void;
  // 标题
  title?: string;
  // 副标题
  subtitle?: string;
  // 是否显示分享按钮
  showShare?: boolean;
  // 分享按钮回调
  onShare?: () => void;
}

// Collapsible Section Component
function CollapsibleSection({
  title,
  icon: Icon,
  iconColor,
  children,
  defaultExpanded = true,
  rightContent,
}: {
  title: string;
  icon: React.ElementType;
  iconColor: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  rightContent?: React.ReactNode;
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  return (
    <div className="flex flex-col min-h-0">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between px-4 py-2 bg-zinc-50/80 border-b border-zinc-200 hover:bg-zinc-100/80 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} weight="fill" />
          <span className="text-xs font-medium text-zinc-700">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {rightContent}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <CaretDown className="w-3.5 h-3.5 text-zinc-400" weight="bold" />
          </motion.div>
        </div>
      </button>

      {/* Collapsible Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className="overflow-hidden flex-1"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const PNG_DATA_URL_PREFIX = 'data:image/png;base64,';

// 检查是否是 diff 结果
function isDiffResult(result: WorkflowExecutionResult | null): boolean {
  if (!result) return false;
  return result.columns.includes('diff_type');
}

function getCellImageSrc(value: unknown): string | null {
  if (value == null) return null;
  const s = typeof value === 'string' ? value.trim() : String(value);
  if (!s) return null;
  if (s.startsWith(PNG_DATA_URL_PREFIX)) return s;
  if (s.length > 50 && !s.includes(' ') && /^[A-Za-z0-9+/=]+$/.test(s)) return PNG_DATA_URL_PREFIX + s;
  return null;
}

// 格式化单元格数据为字符串
function formatCellValue(value: unknown): string {
  if (value instanceof ArrayBuffer) {
    return '[Binary Data]';
  }
  if (ArrayBuffer.isView(value)) {
    return '[Binary Data]';
  }
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    if (value.length > 100 && /[\x00-\x08\x0B\x0C\x0E-\x1F]/.test(value)) {
      return '[Binary Data]';
    }
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (value instanceof Date) {
    return value.toISOString().split('T')[0];
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if ('w' in obj && typeof obj.w === 'string') {
      return obj.w;
    }
    if ('v' in obj) {
      const val = obj.v;
      if (val === null || val === undefined) {
        return '';
      }
      if (typeof val === 'string') {
        return val;
      }
      if (typeof val === 'number') {
        return String(val);
      }
      if (val instanceof Date) {
        return val.toISOString().split('T')[0];
      }
      return String(val);
    }
    try {
      const jsonStr = JSON.stringify(value);
      if (jsonStr.length > 50) {
        return jsonStr.substring(0, 50) + '...';
      }
      return jsonStr;
    } catch {
      return '[Object]';
    }
  }
  return String(value);
}

// Data Table Component
function DataTable({
  data,
  columns,
  maxRows = 5,
}: {
  data: Record<string, unknown>[];
  columns: string[];
  maxRows?: number;
}) {
  const displayData = data.slice(0, maxRows);

  return (
    <div className="flex flex-col h-full">
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 sticky top-0">
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left font-medium text-zinc-600 border-b border-zinc-200 whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayData.map((row, idx) => (
              <tr key={idx} className="hover:bg-zinc-50/50">
                {columns.map((col) => {
                  const cellVal = row[col];
                  const imgSrc = getCellImageSrc(cellVal);
                  return (
                    <td key={col} className="px-3 py-2 text-zinc-700 border-b border-zinc-100 align-middle">
                      {imgSrc ? (
                        <img src={imgSrc} alt="" className="max-h-[72px] max-w-[120px] w-auto h-auto object-contain block" />
                      ) : (
                        <span className="whitespace-nowrap">{formatCellValue(cellVal)}</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>

        {displayData.length === 0 && (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center mb-2">
              <Table className="w-5 h-5 text-zinc-300" />
            </div>
            <p className="text-xs text-zinc-400">暂无数据</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Grouped Results Component
function GroupedResults({
  groups,
  totalRecords,
}: {
  groups: Array<{
    group_key: string;
    columns: string[];
    rows: Record<string, unknown>[];
    rowCount: number;
  }>;
  totalRecords: number;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const { t } = useTranslation();

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex items-center gap-1 px-2 py-2 border-b border-zinc-200 bg-zinc-50/50 overflow-x-auto">
        {groups.map((g, idx) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition-colors ${
              activeTab === idx
                ? 'bg-emerald-100 text-emerald-700'
                : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            {g.group_key}
            <span className="ml-1 text-zinc-400">({g.rowCount})</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {groups[activeTab] && (
          <DataTable
            data={groups[activeTab].rows}
            columns={groups[activeTab].columns}
            maxRows={10}
          />
        )}
      </div>

      {/* Summary */}
      <div className="px-4 py-2 bg-zinc-50 border-t border-zinc-200 text-xs text-zinc-500">
        {t('workflow.totalGroups', { count: groups.length })} · {t('workflow.totalRecords', { count: totalRecords })}
      </div>
    </div>
  );
}

export function WorkflowExecutionResult({
  inputFile,
  result,
  showSaveWorkflow = false,
  onSaveWorkflow,
  onDownloadResult,
  onClose,
  title,
  subtitle,
  showShare = false,
  onShare,
}: WorkflowExecutionResultProps) {
  const { t } = useTranslation();
  const diffViewRef = useRef<DiffResultViewRef | null>(null);

  if (!result) return null;

  const isGrouped = result.is_grouped && result.grouped_results && result.grouped_results.length > 0;
  const totalRecords = isGrouped
    ? result.grouped_results!.reduce((sum, g) => sum + g.rowCount, 0)
    : result.rowCount;
  const isDiff = isDiffResult(result);

  return (
    <motion.div
      className="h-full bg-white border border-zinc-200/50 rounded-2xl overflow-hidden shadow-[0_20px_40px_-15px_rgba(0,0,0,0.05)] flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
    >
      {/* Header */}
      <div className="p-4 border-b border-zinc-100 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-zinc-900 flex items-center justify-center">
              <Eye className="w-5 h-5 text-emerald-400" weight="fill" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-zinc-900 tracking-tight">
                {title || t('nodes.preview.title')}
              </h2>
              <p className="text-xs text-zinc-500">
                {subtitle || t('nodes.preview.subtitle')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {showShare && onShare && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onShare}
                leftIcon={<ShareNetwork className="w-3.5 h-3.5" />}
              >
                分享
              </Button>
            )}
            {showSaveWorkflow && onSaveWorkflow && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onSaveWorkflow}
                leftIcon={<FloppyDisk className="w-3.5 h-3.5" />}
              >
                {t('templates.apply.saveAsWorkflow')}
              </Button>
            )}
            {onDownloadResult && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  onDownloadResult();
                }}
                leftIcon={<DownloadSimple className="w-3.5 h-3.5" />}
              >
                {t('nodes.preview.download')}
              </Button>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="p-1.5 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Upper Section: Excel Input Preview - Collapsible */}
        {inputFile && (
          <div className="flex-1 flex flex-col min-h-0">
            <CollapsibleSection
              title={t('nodes.preview.inputData')}
              icon={Database}
              iconColor="text-emerald-600"
              defaultExpanded={true}
              rightContent={
                <span className="text-[10px] text-zinc-400">
                  {t('nodes.rows', { count: inputFile.rowCount })}
                </span>
              }
            >
              <div className="h-full">
                <DataTable
                  data={inputFile.data}
                  columns={inputFile.columns}
                  maxRows={5}
                />
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* Lower Section: Execution Result */}
        <div className={`flex-1 flex flex-col min-h-0 ${inputFile ? 'border-t border-zinc-200' : ''}`}>
          <CollapsibleSection
            title={isDiff ? t('nodes.diff.title') : t('nodes.preview.executionResult')}
            icon={isDiff ? ArrowsLeftRight : Lightning}
            iconColor={isDiff ? 'text-violet-500' : 'text-amber-500'}
            defaultExpanded={true}
            rightContent={
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-emerald-600 font-medium">
                  {isGrouped
                    ? t('workflow.totalGroups', { count: result.grouped_results!.length })
                    : t('nodes.rows', { count: result.rowCount })}
                </span>
              </div>
            }
          >
            <div className="h-full">
              {isDiff ? (
                <DiffResultView
                  ref={diffViewRef}
                  data={result.rows}
                  columns={result.columns}
                  metadata={result.metadata}
                />
              ) : isGrouped ? (
                <GroupedResults
                  groups={result.grouped_results!}
                  totalRecords={totalRecords}
                />
              ) : (
                <DataTable
                  data={result.rows}
                  columns={result.columns}
                  maxRows={10}
                />
              )}
            </div>
          </CollapsibleSection>
        </div>
      </div>
    </motion.div>
  );
}

export default WorkflowExecutionResult;
